import type { PublicOfferV2 } from '@luxuryescapes/contract-public-offer'
import { buildCancellationPolicies, buildPartiallyRefundableCancellationPolicies } from '@luxuryescapes/lib-refunds'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import uuidV4 from 'lib/string/uuidV4Utils'
import config from 'constants/config'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { mapBedbankBoardCode } from 'lib/hotels/bedbankUtils'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import { applyChannelMarkupAndRoundUpToTen, totalMarkedMemberRooms, totalMarkedRooms } from 'lib/channelMarkup/channelMarkup'
import { resolveChannelMarkupDetails, resolveMemberChannelMarkupDetails } from 'lib/channelMarkup/channelMarkupV2'
import type { ChannelMarkupSource } from 'lib/channelMarkup/channelMarkupV2'
import { toBedbankCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'

export { mapBedbankBoardCode } from 'lib/hotels/bedbankUtils'

function bedGroupsMap(bedGroup: PublicOfferV2.BedGroup): App.RoomBedGroups {
  return {
    id: bedGroup.id,
    description: bedGroup.description,
    configuration: bedGroup.configuration.map(bedGroupsConfigurationMap),
  }
}

function bedGroupsConfigurationMap(configuration: PublicOfferV2.Configuration): App.BedGroupsConfiguration {
  return {
    type: configuration.type,
    size: configuration.size,
    quantity: configuration.quantity,
  }
}

export function mapBedbankRoomBoardCode(boardCode: string | undefined): string {
  switch (boardCode) {
    case 'Daily breakfast':
      return 'BB'
    case 'Daily breakfast & choice of daily lunch or dinner':
      return 'HB'
    case 'Daily breakfast, lunch & dinner':
      return 'FB'
    case 'All inclusive: Daily breakfast, lunch, dinner & drinks':
      return 'AL'
    case 'Room only':
      return 'RO'
    default:
      return 'LE'
  }
}

function getRateInclusions(serverRate: PublicOfferV2.BedbankRate): Array<App.OfferInclusion> {
  return serverRate.inclusions?.map(inclusion => {
    return {
      id: uuidV4(),
      description: inclusion.text,
      symbol: determineOfferFeatureSymbol(inclusion.icon || inclusion.text),
    }
  }) ?? []
}

export default function bedbankRateMap({
  checkIn,
  checkOut,
  timezone,
  rate,
  room,
  offerId,
  markupSource,
}: {
  checkIn: string
  checkOut: string
  timezone?: string
  rate: PublicOfferV2.BedbankRate
  room: App.BedbankPackage
  offerId?: string
  markupSource?: ChannelMarkupSource
}): App.BedbankRate {
  let cancellationPolicy: App.CancellationPolicy = { policies: [], description: [] }
  const locale = getCurrentIntl().locale

  // Derive the markup base from exclusive + taxesAndFees so that propertyFees is
  // never included in the roundUpToNearestTen calculation, matching svc-order.
  const inclusiveBase = rate.totals.exclusive + rate.totals.taxesAndFees
  // Channel markup (LPP): uplift sell prices (rounded to ten, matching svc-order); taxes,
  // fees and RRP value untouched. No-op when not applicable. Thread the offer pricing so v2
  // campaigns gated on the member price can resolve (mirrors the hotel mapper).
  const markupPricing = { total: inclusiveBase, luxPlusPrice: rate.totals.luxPlusInclusive ?? undefined }
  const markupDetails = resolveChannelMarkupDetails(offerId ?? '', OFFER_TYPE_BED_BANK, markupSource, markupPricing)
  const memberMarkupDetails = resolveMemberChannelMarkupDetails(offerId ?? '', OFFER_TYPE_BED_BANK, markupSource, markupPricing)
  const markup = (value: number) => applyChannelMarkupAndRoundUpToTen(value, markupDetails)
  const memberMarkup = (value: number) => applyChannelMarkupAndRoundUpToTen(value, memberMarkupDetails)
  // Every room carries the whole markup; `totals` is summed back from the marked rows so the two
  // always reconcile.
  const occupancyPricing = rate.occupancyPricing.map(op => {
    const { fees: _fees, ...opWithoutFees } = op
    const opInclusiveBase = op.exclusive + op.taxesAndFees
    const inclusive = markup(opInclusiveBase)
    const memberInclusive = op.luxPlusInclusive ? memberMarkup(op.luxPlusInclusive) : 0
    const propertyFees = op.propertyFees ?? 0
    return {
      ...opWithoutFees,
      occupancy: op.occupancy,
      exclusive: markup(op.exclusive),
      inclusive,
      taxesAndFees: op.taxesAndFees,
      salesTax: op.salesTax,
      propertyFees,
      memberExclusive: op.luxPlusExclusive ? memberMarkup(op.luxPlusExclusive) : 0,
      memberInclusive,
      total: inclusive + propertyFees,
      memberTotal: op.luxPlusInclusive ? memberInclusive + propertyFees : undefined,
    }
  })
  const markedExclusive = totalMarkedRooms(occupancyPricing.map(op => op.exclusive), rate.totals.exclusive, markupDetails)
  const markedInclusive = totalMarkedRooms(occupancyPricing.map(op => op.inclusive), inclusiveBase, markupDetails)
  // Member totals sum the rows only when every room carries a member price. `luxPlusExclusive` /
  // `luxPlusInclusive` are optional per row while their `totals` counterparts gate these lines, so
  // a rate can hold a member total with sparser rows; the rows above map an absent member price to
  // 0, and summing those would collapse the member total, the member `% off` and `memberTotal`.
  const markedMemberExclusive = rate.totals.luxPlusExclusive
    ? totalMarkedMemberRooms(occupancyPricing.map(op => op.memberExclusive), rate.totals.luxPlusExclusive, memberMarkupDetails)
    : 0
  const markedMemberInclusive = rate.totals.luxPlusInclusive
    ? totalMarkedMemberRooms(occupancyPricing.map(op => op.memberInclusive), rate.totals.luxPlusInclusive, memberMarkupDetails)
    : 0

  if (timezone) {
    cancellationPolicy = rate.partiallyRefundable
      ? buildPartiallyRefundableCancellationPolicies(
          {
            checkIn,
            checkOut,
            policies: rate.cancellationPolicies,
            nonRefundableDates: rate.nonRefundableDateRanges,
          },
          { timezone, locale, midnightWrap: false },
        )
      : buildCancellationPolicies(rate.cancellationPolicies, {
          timezone,
          regionCode: rate.regionCode,
          currencyCode: rate.currencyCode,
          locale,
          midnightWrap: false,
        })
  }

  // Common properties shared between both payment method cases
  const baseRate = {
    availableRooms: rate.availableRooms,
    bedGroups: rate.bedGroups.map(bedGroupsMap),
    id: rate.id,
    token: rate.token,
    optionId: rate.optionId,
    promotionId: rate.promotionId ?? undefined,
    roomId: room.roomId,
    room,
    refundable: rate.refundable,
    isReserveForZeroEligible: rate.isReserveForZeroEligible,
    partiallyRefundable: rate.partiallyRefundable,
    isFlightBundle: rate.isFlightBundle,
    mobilePromotion: rate.mobilePromotion,
    groupId: rate.groupId ?? undefined,
    corporate: rate.corporate,
    occupancyPricing,
    totals: {
      exclusive: markedExclusive,
      inclusive: markedInclusive,
      propertyFees: rate.totals.propertyFees,
      taxesAndFees: rate.totals.taxesAndFees,
      memberExclusive: markedMemberExclusive,
      memberInclusive: markedMemberInclusive,
      total: markedInclusive + (rate.totals.propertyFees ?? 0),
      memberTotal: rate.totals.luxPlusInclusive ? markedMemberInclusive + (rate.totals.propertyFees ?? 0) : undefined,
    },
    nights: rate.duration,
    cancellationPolicies: rate.cancellationPolicies,
    value: rate.value,
    // Recompute "% off" from the marked price so the badge isn't overstated; clamp at 0 so a
    // marked price above RRP never renders a negative discount. Keep the server value when no
    // markup applies.
    discount: markupDetails.isApplied
      ? Math.max(0, getValueOffPercent(rate.value, markedInclusive))
      : Math.floor(rate.discount * 100),
    memberDiscount: memberMarkupDetails.isApplied && rate.totals.luxPlusInclusive
      ? Math.max(0, getValueOffPercent(rate.value, markedMemberInclusive))
      : (rate.luxPlusDiscount ? Math.floor(rate.luxPlusDiscount * 100) : 0),
    cancellationPolicy: toBedbankCancellationPolicy(cancellationPolicy),
    margin: rate.margin,
    marginAud: rate.marginAud,
    mx: rate.mx,
    supplier: rate.supplier,
    supplierCode: rate.supplierCode,
    boardCode: mapBedbankBoardCode(rate.boardCode),
    inclusions: getRateInclusions(rate),
    paymentScheduleInfo: (config.PAYMENT_SCHEDULE_LPP_ENABLED && rate.paymentSchedule) ? {
      staticSchedule: {
        templateId: rate.paymentSchedule.templateId,
        supplierPaymentNumberOfDays: rate.paymentSchedule.supplierDueDays,
      },
    } : undefined,
    relations: {
      paymentMethods: rate.relations?.paymentMethod ?? [],
    },
  }

  // Use discriminated union to ensure type safety
  if (rate.paymentMethod === 'propertyCollect') {
    return {
      ...baseRate,
      paymentMethod: 'propertyCollect',
      calculationMethod: rate.calculationMethod,
      payAtPropertyTotals: rate.payAtPropertyTotals!, // Required when propertyCollect
    }
  }

  return {
    ...baseRate,
    paymentMethod: 'leCollect',
    calculationMethod: rate.calculationMethod,
  }
}
